import { BaseCachedApi } from '../BaseCachedApi';
import { RecordingApi } from './RecordingApi';
import {
  IRecordingBase,
  ISubscription,
  Recording,
  RecordingPageTypes,
  RecordingsCache,
  RecordingSpace,
} from './RecordingApi.types';
import { SettingsKeys } from '../models/TranslationKeys';
import { AdultType } from '../models/Shared.types';
import { Backend } from '../Backend';
import {
  SubscriptionSettings,
  SubscriptionTypes,
} from '../../Business/detail/actions/epgRecordingActions/SubscriptionSettings.types';
import { Log } from '@lightningjs/sdk';

export class CachedRecordingsApi extends BaseCachedApi {
  protected override _cache: RecordingsCache | undefined;

  constructor(private backend: Backend) {
    super();
    this.updateGlobalSettings();
  }

  private updateGlobalSettings() {
    this.updateExpirations(
      this.backend.globalSettings.numberValue(SettingsKeys.recordingCacheExpiration) ?? 600000,
      this.backend.globalSettings.numberValue(SettingsKeys.recordingCacheErrorExpiration) ?? 300000
    );
  }

  recording(backend: Backend, recordingId: string): Promise<Recording | undefined> {
    const prom = this.checkCache(backend);

    return prom.then(() => {
      return this._cache?.recordings.find(r => r.identifier === recordingId);
    });
  }

  recordingForProgram(backend: Backend, programId: string, channelId: string): Promise<Recording | undefined> {
    const prom = this.checkCache(backend);

    return prom.then(() => {
      let recording;
      this._cache?.recordings.some(rec => {
        if (rec.program === programId && (channelId === undefined || rec.channel === channelId)) {
          recording = rec;
          return backend.sessionInfo?.npvr && recording.device === backend.sessionInfo.npvr.id;
        }

        return false;
      });
      return recording;
    });
  }

  recordingsForSeries(backend: Backend, seriesId: string): Promise<Recording[]> {
    const prom = this.checkCache(backend);

    return prom.then(() => {
      if (this._cache) return this._cache.recordings.filter(rec => rec.series === seriesId);
      return [];
    });
  }

  editRecording(backend: Backend, recording: Recording) {
    // type Recording contains extra properties which are not accepted by the api
    const baseRecording = new IRecordingBase(recording);
    return RecordingApi.editRecording(backend, baseRecording).then(() => {
      this.clearCache();
    });
  }

  createSubscription(
    backend: Backend,
    settings: SubscriptionSettings,
    assetId: string,
    channelId: string
  ): Promise<void> {
    const deviceId = backend.sessionInfo?.npvr?.id;
    const recordingOptions = {
      beginBuffer: settings.beginBuffer,
      endBuffer: settings.endBuffer,
      deleteProtected: settings.deleteProtected,
    };
    if (deviceId) {
      return RecordingApi.createSubscription(
        backend,
        deviceId,
        assetId,
        channelId,
        settings.type,
        recordingOptions
      ).then(() => backend.cachedRecordingsApi.clearCache());
    }
    return Promise.resolve();
  }

  deleteRecording(backend: Backend, recordingId: string) {
    if (backend.sessionInfo?.npvr?.id) {
      return RecordingApi.deleteRecording(backend, recordingId, backend.sessionInfo.npvr.id).then(() => {
        this.clearCache();
      });
    } else {
      throw 'Delete recording: no npvr id';
    }
  }

  deleteRecordings(backend: Backend, seriesId: string, channelId: string): Promise<void> {
    if (backend.sessionInfo?.npvr?.id) {
      return RecordingApi.deleteRecordings(backend, backend.sessionInfo.npvr.id, seriesId, channelId).then(() => {
        this.clearCache();
      });
    } else {
      throw 'Delete recordings: no npvr id';
    }
  }

  editSubscription(backend: Backend, oldDevice: string, type: SubscriptionTypes, subscription: ISubscription) {
    return RecordingApi.editSubscription(backend, oldDevice, subscription, type).then(() => {
      this.clearCache();
    });
  }

  cancelSubscription(backend: Backend, type: SubscriptionTypes, recording: Recording) {
    const requests = [];

    if (recording.isInProgress()) {
      if (backend.sessionInfo?.npvr?.id) {
        requests.push(RecordingApi.deleteRecording(backend, recording.identifier, backend.sessionInfo.npvr.id));
      } else {
        Log.warn('CancelSubscription no npvr id');
      }
    }

    requests.push(RecordingApi.deleteSubscription(backend, recording, type));

    return Promise.all(requests).then(res => {
      this.clearCache();
      return res;
    });
  }

  cancelSeriesSubscription(backend: Backend, seriesId: string, channelId: string): Promise<unknown> {
    return RecordingApi.deleteSeriesSubscription(backend, seriesId, channelId).then(() => {
      this.clearCache();
    });
  }

  subscription(backend: Backend, recording: Recording): Promise<ISubscription | undefined> {
    const prom = this.checkCache(backend);
    return prom.then(() => {
      if (recording.programSubscription)
        return this._cache?.programSubscription(recording.programSubscription, recording.channel);
      else if (recording.seasonSubscription)
        return this._cache?.seasonSubscription(recording.seasonSubscription, recording.channel);
      else if (recording.seriesSubscription)
        return this._cache?.seriesSubscription(recording.seriesSubscription, recording.channel);
      else throw new Error('Unknown subscription');
    });
  }

  override fillCache(backend: Backend): Promise<unknown> {
    this.updateGlobalSettings();
    if (!backend.sessionInfo?.npvr) {
      this.clearCache();
      return Promise.resolve(undefined);
    }
    const deviceId = backend.sessionInfo.npvr.id;
    if (!deviceId) {
      //Use npvr device id, if no npvr device id available, client is not allowed to use recordings
      this.clearCache();
      return Promise.resolve(undefined);
    }
    return RecordingApi.DeviceRecordingsAndSubscriptions(backend, deviceId).then(result => {
      this.clearCache();
      this._cache = new RecordingsCache();
      const expireBuffer = backend.globalSettings.numberValue(SettingsKeys.npvrRecordingAboutToExpireInterval);
      const recs = result.recordings || [];
      this._cache.recordings = recs.map((rec: Recording) => {
        return new Recording(rec, expireBuffer ? expireBuffer : 7);
      });
      this._cache.subscriptions = result.subscriptions;
      this._cache.status = result.status;
      return this._cache;
    });
  }

  availableSpace(backend: Backend): Promise<RecordingSpace | undefined> {
    return this.checkCache(backend).then(() => this._cache?.availableUsedMaxSpace(backend));
  }

  recordingsForType(
    backend: Backend,
    type: RecordingPageTypes,
    showAdult: AdultType
  ): Promise<Recording[] | undefined> {
    return this.checkCache(backend).then(() => this._cache?.recordingsForType(type, showAdult));
  }

  recordingsFolderForType(
    backend: Backend,
    type: RecordingPageTypes,
    showAdult: AdultType,
    seriesId: string,
    channelId: string
  ): Promise<Recording[] | undefined> {
    return this.recordingsForType(backend, type, showAdult).then(pageRecordings =>
      pageRecordings?.filter(rec => rec.series === seriesId && rec.channel === channelId)
    );
  }
}
